import { type components } from '@/generated/internal-api/openapi-types';
import { type Nullable } from '@/model/Common.model';

export type DoseApiId = components['schemas']['DosageApiId.schema'];
export type DosageV1 = components['schemas']['DosageV1.schema'];

export type DosingSessionApiId = components['schemas']['DosingSessionApiId.schema'];
export type DosingSessionStudyGroupV1 = components['schemas']['DosingSessionStudyGroupV1.schema'];
export type DosingSessionCreateV1 = components['schemas']['DosingSessionCreateV1.schema'];
export type DosingPlanCreateV1 = components['schemas']['DosingPlanCreateV1.schema'];

export const DOSE_WEIGHT_UNIT = 'g';

/** Display unit a `doseVolume` column's values are converted into on read. */
export type DoseVolumeUnit = components['schemas']['TreatmentVolumeUnit.schema'];

/** In the order the picker offers them. */
export const DOSE_VOLUME_UNITS: ReadonlyArray<DoseVolumeUnit> = ['mL', 'µL'];

/** A column carries its unit as a plain string, so an unrecognised one must read as no selection. */
export const isDoseVolumeUnit = (value: Nullable<string>): value is DoseVolumeUnit =>
  DOSE_VOLUME_UNITS.includes(value as DoseVolumeUnit);

/**
 * Lifecycle state the server reports in a `doseStatus` cell. `deleted` is a sheet projection
 * derived from the dosage's `deleted_at`; the persisted dosage status remains planned/administered.
 */
export type DoseStatus = 'planned' | 'administered' | 'deleted';

export const DOSE_STATUS_PLANNED: DoseStatus = 'planned';

/**
 * Written to a `doseStatus` cell to administer the dose, and the status the server reports back
 * afterwards. Receiving it, the server resolves the dose's `collected_at` from the row's `dosedAt` —
 * falling back to the current time when `dosedAt` is unset — and locks the dose from further edits.
 */
export const DOSE_STATUS_ADMINISTERED: DoseStatus = 'administered';
export const DOSE_STATUS_DELETED: DoseStatus = 'deleted';

const DOSE_STATUS_DISPLAY: Record<DoseStatus, string> = {
  planned: 'Planned',
  administered: 'Administered',
  deleted: 'Deleted',
};

/** Label shown on a `doseStatus` chip. */
export const getDoseStatusDisplay = (status: DoseStatus): string => DOSE_STATUS_DISPLAY[status];

/**
 * Whether a `doseStatus` cell value means the dose has been administered. Every dose carries a
 * status — one awaiting administration reports `planned`, not a blank cell — so only
 * {@link DOSE_STATUS_ADMINISTERED} counts, which is also what the cell writes when administering, so
 * the chip appears optimistically. `DoseStatusCell` additionally treats the server's
 * `dose_administered` lock as administered, so a status this does not recognise cannot offer to
 * administer a dose the server has already closed.
 */
export const isDoseAdministered = (value: Nullable<string>): boolean => value === DOSE_STATUS_ADMINISTERED;

export const isDosePlanned = (value: Nullable<string>): boolean => value === DOSE_STATUS_PLANNED;

export const isDoseDeleted = (value: Nullable<string>): boolean => value === DOSE_STATUS_DELETED;
